import { useState, useEffect, useCallback, useRef } from 'react'
import rt, { releaseSocket } from './index.js'

/**
 * React hook for managing realtime socket connections
 * @param {string} namespace - The namespace to connect to
 * @param {Object} options - Socket connection options
 * @returns {Object} Socket connection state and utilities
 */
export const useRealtime = (namespace, options = {}) => {
  const [socket, setSocket] = useState(null)
  const [connected, setConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState(null)

  const socketRef = useRef(null)
  const cleanupFunctionsRef = useRef([])
  const optionsRef = useRef(options)
  
  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options
  }, [options])

  const connect = useCallback(() => {
    if (!namespace || socketRef.current) return

    setConnecting(true)
    setError(null)

    try {
      const newSocket = rt(namespace, optionsRef.current)

      // Connection event handlers
      const handleConnect = () => {
        setConnected(true)
        setConnecting(false)
        setError(null)
      }

      const handleDisconnect = (reason) => {
        setConnected(false)
        setConnecting(false)
      }

      const handleConnectError = (err) => {
        setConnected(false)
        setConnecting(false)
        setError(err.message)
      }

      const handleReconnect = () => {
        setConnected(true)
        setError(null)
      }

      // Attach event listeners
      newSocket.on('connect', handleConnect)
      newSocket.on('disconnect', handleDisconnect)
      newSocket.on('connect_error', handleConnectError)
      newSocket.on('reconnect', handleReconnect)

      // Store cleanup functions
      cleanupFunctionsRef.current = [
        () => newSocket.off('connect', handleConnect),
        () => newSocket.off('disconnect', handleDisconnect),
        () => newSocket.off('connect_error', handleConnectError),
        () => newSocket.off('reconnect', handleReconnect)
      ]

      socketRef.current = newSocket
      setSocket(newSocket)

      // Set initial connection state
      if (newSocket.connected) {
        setConnected(true)
        setConnecting(false)
      }

    } catch (err) {
      setError(err.message)
      setConnecting(false)
    }
  }, [namespace]) // Remove options dependency to prevent infinite loops

  const disconnect = useCallback(() => {
    // Clean up event listeners
    cleanupFunctionsRef.current.forEach(cleanup => cleanup())
    cleanupFunctionsRef.current = []

    if (socketRef.current) {
      // The socket is pooled per namespace: disconnecting it here used to
      // close it under every other component watching the same one. Let go
      // of it instead — it closes when the last holder does.
      releaseSocket(namespace)
      socketRef.current = null
      setSocket(null)
      setConnected(false)
      setConnecting(false)
    }
  }, [namespace])

  // Connect on mount and namespace/options change
  useEffect(() => {
    if (namespace) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [namespace]) // Remove function dependencies to prevent infinite loops

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    socket,
    connected,
    connecting,
    error,
    connect,
    disconnect
  }
}